import { JSONSchema7 } from 'json-schema'
import { call, select } from 'redux-saga/effects'

import { getFormSchema } from '../../../api/takeoff-api'
import { Region } from '../../../models/region'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import { GENERAL_CACHE, selectFormCache } from '../../slices/forms'

/**
 * Retrieve the form schema for a given material type.
 * Checks the form cache first; falls back to fetching from the backend API.
 * Returns null if the schema is unavailable.
 */
export function* getSchemaForFloorLevel(materialType: DRAWABLE_TYPES | null, buildingId: string) {
    const formCache = yield select(selectFormCache)
    const cached = (formCache?.[buildingId]?.[materialType as string]?.schema as JSONSchema7) ?? null

    if (cached || !materialType) return cached

    try {
        return yield call(getFormSchema, materialType, buildingId)
    } catch {
        return null
    }
}

/**
 * Extract floor_level enum values from a JSON schema.
 * Scans both `definitions` and `properties` for the exact key `floor_level`.
 * If the property uses a $ref, resolves it against schema.definitions.
 * Returns the enum values if found.
 */
export function getFloorLevelEnumValues(schema: JSONSchema7 | null | undefined): string[] | null {
    if (!schema) return null

    for (const source of [schema.definitions, schema.properties]) {
        if (!source) continue

        for (const key of Object.keys(source)) {
            if (key === 'floor_level') {
                const entry = source[key] as { enum?: string[]; $ref?: string } | undefined

                if (entry?.enum?.length) return entry.enum

                if (entry?.$ref) {
                    const defName = entry.$ref.split('/').pop()

                    if (defName && schema.definitions) {
                        const def = schema.definitions[defName] as { enum?: string[] } | undefined

                        if (def?.enum?.length) return def.enum
                    }
                }
            }
        }
    }

    return null
}

/**
 * Match a candidate name (from region or page name) against floor_level enum values.
 * Comparison is case-insensitive. Returns the matched enum value or null.
 */
export function matchFloorLevelFromContext(
    enumValues: string[],
    regionId: number | null,
    regions: Region[],
    pageName: string
): string | null {
    const candidate = regionId ? regions.find((r) => r.id === regionId)?.name ?? '' : pageName

    if (!candidate) return null

    return enumValues.find((val) => val.toLowerCase() === candidate.toLowerCase()) ?? null
}

export { GENERAL_CACHE }
